import { Suspense, lazy, ReactNode } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { StoreProvider } from './contexts/StoreContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './i18n/LanguageContext';
import { ConfirmProvider } from './hooks/useConfirm';
import './i18n';
import Login from './pages/Login';
import Layout from './components/Layout';
import PageLoader from './components/ui/PageLoader';

// Lazy-loaded page components for code splitting
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Inventory = lazy(() => import('./pages/Inventory'));
const Sales = lazy(() => import('./pages/Sales'));
const Expenses = lazy(() => import('./pages/Expenses'));
const Users = lazy(() => import('./pages/Users'));
const Roles = lazy(() => import('./pages/Roles'));
const Customers = lazy(() => import('./pages/Customers'));
const Categories = lazy(() => import('./pages/Categories'));
const Repairs = lazy(() => import('./pages/Repairs'));
const Quotes = lazy(() => import('./pages/Quotes'));
const Invoices = lazy(() => import('./pages/Invoices'));
const Returns = lazy(() => import('./pages/Returns'));
const Bundles = lazy(() => import('./pages/Bundles'));
const Commissions = lazy(() => import('./pages/Commissions'));
const Logs = lazy(() => import('./pages/Logs'));
const Settings = lazy(() => import('./pages/Settings'));
const NotFound = lazy(() => import('./pages/NotFound'));

interface PrivateRouteProps {
  children: ReactNode;
}

function PrivateRoute({ children }: PrivateRouteProps) {
  const { currentUser } = useAuth();
  return currentUser ? <>{children}</> : <Navigate to="/login" />;
}

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <Router>
          <AuthProvider>
            <StoreProvider>
              <ConfirmProvider>
                <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route
                    path="/"
                    element={
                      <PrivateRoute>
                        <Layout />
                      </PrivateRoute>
                    }
                  >
                    <Route index element={
                      <Suspense fallback={<PageLoader fullScreen={false} />}>
                        <Dashboard />
                      </Suspense>
                    } />
                    <Route path="inventory" element={
                      <Suspense fallback={<PageLoader fullScreen={false} />}>
                        <Inventory />
                      </Suspense>
                    } />
                    <Route path="sales" element={
                      <Suspense fallback={<PageLoader fullScreen={false} />}>
                        <Sales />
                      </Suspense>
                    } />
                    <Route path="expenses" element={
                      <Suspense fallback={<PageLoader fullScreen={false} />}>
                        <Expenses />
                      </Suspense>
                    } />
                    <Route path="users" element={
                      <Suspense fallback={<PageLoader fullScreen={false} />}>
                        <Users />
                      </Suspense>
                    } />
                    <Route path="roles" element={
                      <Suspense fallback={<PageLoader fullScreen={false} />}>
                        <Roles />
                      </Suspense>
                    } />
                    <Route path="customers" element={
                      <Suspense fallback={<PageLoader fullScreen={false} />}>
                        <Customers />
                      </Suspense>
                    } />
                    <Route path="categories" element={
                      <Suspense fallback={<PageLoader fullScreen={false} />}>
                        <Categories />
                      </Suspense>
                    } />
                    <Route path="repairs" element={
                      <Suspense fallback={<PageLoader fullScreen={false} />}>
                        <Repairs />
                      </Suspense>
                    } />
                    <Route path="quotes" element={
                      <Suspense fallback={<PageLoader fullScreen={false} />}>
                        <Quotes />
                      </Suspense>
                    } />
                    <Route path="invoices" element={
                      <Suspense fallback={<PageLoader fullScreen={false} />}>
                        <Invoices />
                      </Suspense>
                    } />
                    <Route path="returns" element={
                      <Suspense fallback={<PageLoader fullScreen={false} />}>
                        <Returns />
                      </Suspense>
                    } />
                    <Route path="bundles" element={
                      <Suspense fallback={<PageLoader fullScreen={false} />}>
                        <Bundles />
                      </Suspense>
                    } />
                    <Route path="commissions" element={
                      <Suspense fallback={<PageLoader fullScreen={false} />}>
                        <Commissions />
                      </Suspense>
                    } />
                    <Route path="logs" element={
                      <Suspense fallback={<PageLoader fullScreen={false} />}>
                        <Logs />
                      </Suspense>
                    } />
                    <Route path="settings" element={
                      <Suspense fallback={<PageLoader fullScreen={false} />}>
                        <Settings />
                      </Suspense>
                    } />
                    <Route path="*" element={
                      <Suspense fallback={<PageLoader fullScreen={false} />}>
                        <NotFound />
                      </Suspense>
                    } />
                  </Route>
                </Routes>
              </ConfirmProvider>
            </StoreProvider>
          </AuthProvider>
        </Router>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
